import { environment } from "$lib/env";
import type { IBalance } from "$lib/interfaces/IBalance";
import { getCookie } from "$lib/utils/cookies";

interface ApiResponse<T> {
  data: T;
  error?: string;
}

class GameService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = environment.apiUrl;
  }

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new Headers(options.headers);

    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const token = getCookie("forza_token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(url, { ...options, headers });
    const data = await response.json();

    if (!response.ok) {
      return { data: null as T, error: data.message || "An error occurred" };
    }

    return { data };
  }

  async initGame(slug: any, isDemo: any): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/games/${slug}/launch?demo=${isDemo}`, {
      method: "POST",
    });
  }

  async getGames(
    query = "",
    page = 0,
    provider = "",
    platform = "MOBILE_SUPPORT",
  ): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(
      `/games?size=32&page=${page}&name=${query}&gameProviderId=${provider}&platformSupport=${platform}`,
      {
        method: "GET",
      },
    );
  }

  async getRandomGames(): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/games/random`, {
      method: "GET",
    });
  }

  async getLastWins(): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/latest-bets?size=12`, {
      method: "GET",
    });
  }

  async searchGames(query = ""): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(
      `/search?size=30&page=0&query=${query}&platformSupport=MOBILE_SUPPORT`,
      {
        method: "GET",
      },
    );
  }

  async addToFavorite(gameId: any): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/games/${gameId}/favorites`, {
      method: "POST",
    });
  }

  async deleteFromFavorites(gameId: any): Promise<ApiResponse<any[]>> {
    return this.fetchWithAuth<any[]>(`/games/${gameId}/favorites`, {
      method: "DELETE",
    });
  }

  public async getAllGames(data: {
    page: number;
    size: number;
    gameCategoryId?: number;
    gameProviderSlug?: string;
    name?: string;
  }) {
    const queryString = new URLSearchParams(
      data as Record<string, any>,
    ).toString();
    return this.fetchWithAuth<any[]>(`/games?${queryString}`);
  }

  public async getFavouriteGames(page = 0, size = 14) {
    const queryString = new URLSearchParams({ page, size } as Record<
      string,
      any
    >).toString();
    return this.fetchWithAuth<any[]>(`/games/favorites?${queryString}`);
  }

  public async getLastPlayedGames(page = 0, size = 14) {
    const queryString = new URLSearchParams({ page, size } as Record<
      string,
      any
    >).toString();
    return this.fetchWithAuth<any[]>(`/games/last-played?${queryString}`);
  }
}

export const gameService = new GameService();
